import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/*
 * MOKY 29.10.2026:
 * - Codes are compared without caring about upper/lower case and spaces.
 * - A code Shopify will not apply (minimum order not reached, already used...)
 *   now shows a clear, translated message naming the code (it used to name "test").
 * - 30.10.2026: never reloads the page. After a code is added or removed, every
 *   cart on the page (drawer and cart page) re-renders itself through the theme's
 *   discount event; this section is also morphed in place when possible.
 * - A code that does not combine with the automatic order discount gets its own
 *   message instead of the generic one.
 * - A code Shopify keeps on the cart but is not applying right now (e.g. NEW10 once
 *   the cart passes CHF 100 and the automatic 10% order discount takes over) is not
 *   in the server-rendered pills, so it is added here as a greyed pill with the
 *   reason, read from /cart.js. It applies again by itself below CHF 100.
 * - The welcome code (NEW10) also switches the "price with code" display on/off
 *   (snippets/moky-code-prices.liquid).
 */
const WELCOME_CODE = 'NEW10';
const WELCOME_KEY = 'moky-welcome-offer';

const ERRORS_AUTO = {
  en: 'Your order already has an automatic discount, which can’t be combined with {code}.',
  de: 'Deine Bestellung hat bereits einen automatischen Rabatt, der nicht mit {code} kombinierbar ist.',
  fr: 'Votre commande bénéficie déjà d’une remise automatique, non cumulable avec {code}.',
  it: 'Il tuo ordine ha già uno sconto automatico, non cumulabile con {code}.',
  es: 'Tu pedido ya tiene un descuento automático que no se puede combinar con {code}.',
  pt: 'A sua encomenda já tem um desconto automático, que não é acumulável com {code}.',
  nl: 'Je bestelling heeft al een automatische korting die niet te combineren is met {code}.',
  pl: 'Twoje zamówienie ma już rabat automatyczny, którego nie można łączyć z {code}.',
};

const IDLE_NOTES = {
  en: ['Order discount applied instead', 'Not active on this cart'],
  de: ['Stattdessen gilt der Bestellrabatt', 'Für diesen Warenkorb nicht aktiv'],
  fr: ['Remise de commande appliquée à la place', 'Non actif sur ce panier'],
  it: ['Al suo posto vale lo sconto ordine', 'Non attivo su questo carrello'],
  es: ['Se aplica el descuento del pedido', 'No activo en este carrito'],
  pt: ['Aplica-se o desconto da encomenda', 'Não ativo neste carrinho'],
  nl: ['In plaats daarvan geldt de orderkorting', 'Niet actief op deze winkelwagen'],
  pl: ['Zamiast tego obowiązuje rabat na zamówienie', 'Nieaktywny w tym koszyku'],
};

function lang() {
  return (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
}

/** Any cart-level discount that is not a code (the automatic order discount). */
function hasAutomatic(cart) {
  return (cart?.cart_level_discount_applications || []).some(
    (/** @type {{ type: string }} */ app) => app.type !== 'discount_code'
  );
}

const ERRORS = {
  en: 'The code {code} can’t be used on this cart. Check its conditions, e.g. the minimum order.',
  de: 'Der Code {code} gilt für diesen Warenkorb nicht. Bitte prüfe die Bedingungen, z. B. den Mindestbestellwert.',
  fr: 'Le code {code} ne s’applique pas à ce panier. Vérifiez ses conditions, par ex. le montant minimum.',
  it: 'Il codice {code} non è valido per questo carrello. Controlla le condizioni, ad es. l’ordine minimo.',
  es: 'El código {code} no se puede usar en este carrito. Revisa sus condiciones, p. ej. el pedido mínimo.',
  pt: 'O código {code} não pode ser usado neste carrinho. Verifique as condições, p. ex. a encomenda mínima.',
  nl: 'De code {code} geldt niet voor deze winkelwagen. Controleer de voorwaarden, bijv. het minimale bestelbedrag.',
  pl: 'Kodu {code} nie można użyć w tym koszyku. Sprawdź warunki, np. minimalną wartość zamówienia.',
};

/** @param {string} code */
function same(a, b) {
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

/** @param {boolean} on */
function setWelcomeFlag(on) {
  try {
    const state = JSON.parse(localStorage.getItem(WELCOME_KEY) || '{}') || {};
    if (on) state.applied = 1;
    else delete state.applied;
    localStorage.setItem(WELCOME_KEY, JSON.stringify(state));
  } catch (error) {
    /* storage blocked: the display just stays as it is */
  }
  document.documentElement.classList.toggle('moky-code-on', on);
}

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('cart:update', this.#syncLater);
    document.addEventListener('discount:update', this.#syncLater);
    this.#syncIdle();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('cart:update', this.#syncLater);
    document.removeEventListener('discount:update', this.#syncLater);
  }

  /** after the carts have re-rendered */
  #syncLater = () => {
    window.setTimeout(() => this.#syncIdle(), 700);
  };

  /** Adds (or removes) the greyed pills for codes that are on the cart but not applying. */
  async #syncIdle() {
    const list = this.querySelector('.cart-discount__codes');
    if (!list) return;

    let cart;
    try {
      cart = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } }).then(
        (r) => r.json()
      );
    } catch (error) {
      return;
    }

    const idle = (cart.discount_codes || []).filter((/** @type {{ applicable: boolean }} */ d) => !d.applicable);

    for (const pill of list.querySelectorAll('.cart-discount__pill[data-moky-idle]')) {
      const code = pill instanceof HTMLElement ? pill.dataset.discountCode : '';
      if (!idle.some((/** @type {{ code: string }} */ d) => same(d.code, code))) pill.remove();
    }

    const shown = this.#existingDiscounts();
    const notes = IDLE_NOTES[/** @type {keyof typeof IDLE_NOTES} */ (lang())] || IDLE_NOTES.en;
    const note = hasAutomatic(cart) ? notes[0] : notes[1];
    const closeIcon = this.querySelector('.cart-discount__pill-remove svg')?.outerHTML ||
      '<svg viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

    for (const d of idle) {
      if (shown.some((code) => same(code, d.code))) continue;
      const li = document.createElement('li');
      li.className = 'cart-discount__pill is-idle';
      li.dataset.discountCode = d.code;
      li.dataset.mokyIdle = '';
      const code = document.createElement('p');
      code.className = 'cart-discount__pill-code';
      code.textContent = d.code;
      const text = document.createElement('span');
      text.className = 'cart-discount__pill-note';
      text.textContent = note;
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'cart-discount__pill-remove svg-wrapper svg-wrapper--smaller button-unstyled';
      remove.setAttribute('aria-label', `Remove ${d.code}`);
      remove.innerHTML = closeIcon;
      remove.addEventListener('click', this.removeDiscount);
      li.append(code, text, remove);
      list.appendChild(li);
    }
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement)) return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    const sectionId = typeof this.dataset.sectionId === 'string' ? this.dataset.sectionId : '';
    const abortController = this.#createAbortController();
    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.some((code) => same(code, discountCodeValue))) {
        discountCode.value = '';
        return;
      }

      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add('hidden');
      cartDiscountErrorShipping.classList.add('hidden');

      /** @type {Record<string, unknown>} */
      const body = { discount: [...existingDiscounts, discountCodeValue].join(',') };
      if (sectionId) body.sections = [sectionId];

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('discount update failed');
      const data = await response.json();

      const entry = (data.discount_codes || []).find(
        (/** @type {{ code: string; applicable: boolean; }} */ discount) => same(discount.code, discountCodeValue)
      );

      if (!entry || entry.applicable === false) {
        discountCode.value = '';
        // No re-render here, so the message stays readable. The code stays on the cart
        // (it can apply later), shown as a greyed pill.
        this.#handleDiscountError(hasAutomatic(data) ? 'auto' : 'discount_code', discountCodeValue);
        this.#syncIdle();
        return;
      }

      if (same(discountCodeValue, WELCOME_CODE)) setWelcomeFlag(true);

      const newHtml = sectionId && data.sections ? data.sections[sectionId] : null;
      if (!newHtml) {
        discountCode.value = '';
        document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
        return;
      }

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code))
        ) {
          this.#handleDiscountError('shipping');
          discountCode.value = '';
          return;
        }
      }

      discountCode.value = '';
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      this.#morph(sectionId, data);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        this.#handleDiscountError('discount_code', discountCodeValue);
      }
    } finally {
      this.#activeFetch = null;
      if (button instanceof HTMLButtonElement) button.disabled = false;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement)
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const sectionId = typeof this.dataset.sectionId === 'string' ? this.dataset.sectionId : '';
    const abortController = this.#createAbortController();

    try {
      /** @type {Record<string, unknown>} */
      const body = { discount: existingDiscounts.join(',') };
      if (sectionId) body.sections = [sectionId];

      const response = await fetch(Theme.routes.cart_update_url, {
        ...fetchConfig('json', { body: JSON.stringify(body) }),
        signal: abortController.signal,
      });

      if (!response.ok) throw new Error('discount update failed');
      const data = await response.json();

      if (same(discountCode, WELCOME_CODE)) setWelcomeFlag(false);

      pill.remove();
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      this.#morph(sectionId, data);
    } catch (error) {
      /* nothing changed on the cart: leave the pill where it is */
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Morphs this section in place when the response carries it and it is on the
   * page. Never throws: the discount event already re-renders the carts.
   * @param {string} sectionId
   * @param {Record<string, any>} data
   */
  #morph(sectionId, data) {
    const html = sectionId && data.sections ? data.sections[sectionId] : null;
    if (!html || !document.getElementById(`shopify-section-${sectionId}`)) return;
    Promise.resolve()
      .then(() => morphSection(sectionId, html))
      .catch(() => {});
  }

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'auto' | 'shipping'} type - The type of discount error.
   * @param {string} [code] - The code that was tried.
   */
  #handleDiscountError(type, code) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'shipping' ? cartDiscountErrorShipping : cartDiscountErrorDiscountCode;
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    if (type !== 'shipping' && code) {
      const table = type === 'auto' ? ERRORS_AUTO : ERRORS;
      const message = table[/** @type {keyof typeof ERRORS} */ (lang())] || table.en;
      target.textContent = message.replace('{code}', code.toUpperCase());
    }
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
